/**
 * THE REFUSAL OF A ROLE ASSIGNMENT, RENDERED. OA-0001 §3.3 / §3.11, 08 §7.
 *
 * The sibling of `<CapacityRefusalNotice>`, and it is a sibling on purpose: a commercial limit and a
 * delegation ceiling are both rules the server owns, both refuse a WRITE without touching anything
 * that already exists, and both are worthless to a customer unless the refusal names what was
 * refused. So the two look the same on screen and neither is ever a generic error.
 *
 * Nothing here decides anything. It is handed a refusal the server produced and renders the sentences
 * `api/roleAssignmentText.ts` writes from it — including the list of capabilities, in the catalogue's
 * own Hebrew, which is the part that makes the refusal actionable rather than merely polite.
 */

import type { RoleAssignmentRefusal } from '../api/contract';
import { roleAssignmentRefusalText } from '../api/roleAssignmentText';

export function RoleRefusalNotice({
  refusal,
  announce = true,
}: {
  readonly refusal: RoleAssignmentRefusal;
  /**
   * `false` suppresses the live-region role. The refusal is an ALERT when it answers something the
   * user just did, and merely a sample when it is displayed as one — announcing a sample on page
   * load is noise a screen-reader user cannot dismiss (09 §9.5).
   */
  readonly announce?: boolean;
}): JSX.Element {
  const text = roleAssignmentRefusalText(refusal);
  return (
    <section className="denial" data-tone="blocked" {...(announce ? { role: 'alert' } : {})}>
      <h2 className="denial__title">{text.titleHe}</h2>
      <p className="denial__explanation">{text.explanationHe}</p>

      {text.refusedHeadingHe === null ? null : (
        <>
          <p className="refusal__heading">{text.refusedHeadingHe}</p>
          <ul className="refusal__capabilities">
            {text.namedHe.map((he) => (
              <li key={he} className="refusal__capability">
                {he}
              </li>
            ))}
          </ul>
          {text.unnamedHe === null ? null : <p className="refusal__unnamed">{text.unnamedHe}</p>}
        </>
      )}

      {/* OA-0001 §3.11's other half: an administrator told only "refused" fears a half-done change. */}
      <p className="denial__explanation">{text.unchangedHe}</p>
      <p className="denial__next-step">{text.nextStepHe}</p>
    </section>
  );
}
